import {
  Box,
  Button,
  ButtonGroup,
  ComponentMultiStyleConfig,
  Flex,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import {
  SearchDropdownDisplayValue,
  SearchDropdownOption,
} from "../../../../../lib";
import { ComponentStyleProps } from "../../../../../lib/styles/props/component-style-props";
import { CMSearchAndSelectTextbox } from "../../../../_components";

export type CMFormInputSearchAndSelectPopupProps<T extends string | number> = {
  /**
   * プレースホルダーの文字
   */
  placeholder?: string;

  /**
   * 入力可能な最大文字数。
   */
  maxLength?: number;

  /**
   * メニューの最大表示数
   */
  menuShowMaxCount?: number;

  /**
   * 確定ボタンのラベル
   */
  labelSubmit?: string;

  /**
   * クリアボタンのラベル
   */
  labelClear?: string;

  /**
   * クリアボタンの表示制御
   */
  hideClearButton?: boolean;

  searchResultNone?: string;

  /**
   * 値を表示値に変換する関数。
   * heading: 各行の手前に、薄い字で表示する部分。必須ではない。
   * main: 表示値。必須。
   */
  toDisplayValue: (value: T) => Promise<SearchDropdownDisplayValue>;

  /**
   * テキストボックスに自由入力されている文字列。
   */
  inputText?: string | null;
  onChangeInputText?: (value: string) => void;
  onClearInputText?: () => void;

  /**
   * ドロップダウンに表示する選択肢一覧。
   */
  options: SearchDropdownOption<T>[];
  /**
   * ドロップダウンから選択肢を選択したときに呼び出される関数。
   */
  onSelectOption: (value: T) => void;
  /**
   * 内部で選択中の値。
   */
  internalSelectedValue: T | null;

  /**
   * この検索条件全体を確定したときに呼び出される関数。
   */
  onSubmit: () => void;
  /**
   * この検索条件全体をクリアしたときに呼び出される関数。
   */
  onClear: () => void;

  /**
   * メニューの表示制御に使用する。
   */
  searchResultMenuSettings: {
    isOpenMenu: boolean;
    onCloseMenu: () => void;
  };
} & ComponentStyleProps;

/**
 * フリーテキスト検索条件を表示するコンポーネント
 */
export const CMFormInputSearchAndSelectPopup = <T extends string | number>({
  placeholder,
  maxLength = 100,
  menuShowMaxCount,

  labelSubmit = "",
  labelClear = "",
  hideClearButton = false,
  searchResultNone = "",

  toDisplayValue,

  inputText,
  onChangeInputText,
  onClearInputText,

  options,
  onSelectOption,
  internalSelectedValue,

  onSubmit,
  onClear,

  searchResultMenuSettings,

  sx,
  ...rest
}: CMFormInputSearchAndSelectPopupProps<T>) => {
  const styles = useMultiStyleConfig("CMFormInputSearchAndSelectPopupStyles");
  const containerStyles = useMemo(
    () => ({
      ...styles.container,
      ...sx,
    }),
    [styles.container, sx],
  );
  return (
    <Flex sx={containerStyles} {...rest}>
      <Box sx={styles.childContainer}>
        <Box sx={styles.children}>
          <CMSearchAndSelectTextbox
            options={options}
            onSelectOption={onSelectOption}
            selected={internalSelectedValue}
            placeholder={placeholder}
            maxLength={maxLength}
            menuShowMaxCount={menuShowMaxCount}
            inputText={inputText}
            toDisplayValue={toDisplayValue}
            onChangeInputText={onChangeInputText}
            onClearInputText={onClearInputText}
            searchResultNone={searchResultNone ?? ""}
            searchResultMenuSettings={searchResultMenuSettings}
          />
        </Box>
        <ButtonGroup sx={styles.buttonGroup}>
          <Button onClick={onSubmit} sx={styles.submitButton}>
            <Text sx={styles.labelSubmit}>{labelSubmit}</Text>
          </Button>
          {hideClearButton ? null : (
            <Button onClick={onClear} sx={styles.clearButton}>
              <Text sx={styles.labelClear}>{labelClear}</Text>
            </Button>
          )}
        </ButtonGroup>
      </Box>
    </Flex>
  );
};

export const CMFormInputSearchAndSelectPopupStyles: ComponentMultiStyleConfig =
  {
    parts: [
      "container",
      "childContainer",
      "children",
      "buttonGroup",
      "submitButton",
      "labelSubmit",
      "clearButton",
      "labelClear",
    ],
    baseStyle: {
      // container: { bg: "white" },
      childContainer: {},
      children: {},
      buttonGroup: {
        mt: "12px",
      },
      submitButton: {
        bg: "teal.500",
        color: "white",
        h: "24px",
        maxW: "200px",
        minW: "52px",
        fontSize: "16px",
      },
      labelSubmit: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      },
      clearButton: {
        bg: "gray.200",
        color: "gray.700",
        h: "24px",
        maxW: "200px",
        minW: "52px",
        fontSize: "16px",
      },
      labelClear: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      },
    },
  };
